import { CameraOutlined, FolderOpenOutlined } from "@ant-design/icons";
import { Drawer, Upload, message } from "antd";
import React, { useRef, useState } from "react";
import { uploadFileToS3 } from "../helpers/awsUpload";
import {
  getNativePlatform,
  isNativeApp,
  requestCameraPermission,
} from "../helpers/nativePermissions";
import { DecriptEnv } from "../helpers/utils";
import PermissionDeniedModal from "./PermissionDeniedModal";

const { Dragger } = Upload;

const isAndroid = /Android/i.test(navigator.userAgent);

interface S3FileUploaderProps {
  bucket?: string;
  folder?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  maxSize?: number; // In bytes
  onUploadStart?: (file: File) => void;
  // ✅ Updated Interface: first arg is now 'key' (relative path)
  onSuccess?: (key: string, fileName: string) => void;
  onError?: (error: any) => void;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const S3FileUploader: React.FC<S3FileUploaderProps> = ({
  bucket = DecriptEnv(process.env.REACT_APP_AWS_BUCKET_NAME),
  folder = "documents",
  accept = "*",
  multiple = false,
  disabled = false,
  maxSize,
  onUploadStart,
  onSuccess,
  onError,
  children,
  className,
  style,
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [permissionModal, setPermissionModal] = useState<{
    visible: boolean;
    name: string;
  }>({ visible: false, name: "" });

  const processUpload = async (options: any) => {
    setPermissionModal({ visible: false, name: "" })
    const file = options.file as File;

    if (maxSize && file.size > maxSize) {
      const maxMb = Math.round(maxSize / (1024 * 1024));
      message.error(
        `${file.name} is too large. Maximum allowed size is ${maxMb}MB.`,
      );
      options.onError(new Error("File too large"));
      if (onError) onError(new Error("File too large"));
      return;
    }

    try {
      if (onUploadStart) onUploadStart(file);

      const result = await uploadFileToS3(file, bucket, folder);

      message.success(`${file.name} uploaded successfully!`);

      options.onSuccess(result, file);

      if (onSuccess) {
        // ✅ CHANGE: Passing 'result.Key' (relative path) instead of Location
        onSuccess(result.Key, file.name);
      }
    } catch (err: any) {
      console.error(err);
      message.error(`${file.name} upload failed.`);
      options.onError(err);
      if (onError) onError(err);
    }
  };

  const processFiles = async (files: FileList | null) => {
    setPermissionModal({ visible: false, name: "" })
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (maxSize && file.size > maxSize) {
        const maxMb = Math.round(maxSize / (1024 * 1024));
        message.error(
          `${file.name} is too large. Maximum allowed size is ${maxMb}MB.`,
        );
        if (onError) onError(new Error("File too large"));
        continue;
      }

      try {
        if (onUploadStart) onUploadStart(file);

        const result = await uploadFileToS3(file, bucket, folder);

        message.success(`${file.name} uploaded successfully!`);

        if (onSuccess) {
          onSuccess(result.Key, file.name);
        }
      } catch (err: any) {
        console.error(err);
        message.error(`${file.name} upload failed.`);
        if (onError) onError(err);
      }
    }
  };

  const handleIOSPicker = async () => {
    const granted = await requestCameraPermission();
    if (!granted) {
      setPermissionModal({ visible: true, name: "Camera" });
      return;
    }
    setTimeout(() => {
      hiddenInputRef.current && hiddenInputRef.current.click();
    }, 100);
  };

  return (
    <>
      {isAndroid ? (
        <>
          {/* Camera input - captures image from camera (single file only as camera can't do multi) */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => {
              processFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {/* Gallery input - uses the provided accept prop, supports multiple when enabled */}
          <input
            ref={hiddenInputRef}
            type="file"
            accept={accept}
            style={{ display: "none" }}
            multiple={multiple}
            onChange={(e) => {
              processFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <div
            onClick={(e) => {
              if (isAndroid && !disabled) {
                e.preventDefault();
                e.stopPropagation();
                setOpen(true);
              }
            }}
            onDragEnter={() => {
              if (isAndroid) setDragging(true);
            }}
            onDragLeave={() => {
              if (isAndroid) setDragging(false);
            }}
            onDrop={() => {
              if (isAndroid) setDragging(false);
            }}
          >
            <Dragger
              accept={accept}
              multiple={multiple}
              showUploadList={false}
              customRequest={processUpload}
              disabled={disabled || (isAndroid && !dragging)}
              className={className}
              style={style}
            >
              {children}
            </Dragger>
          </div>
        </>
      ) : (
        <div
          onClick={async (e) => {
            if (isNativeApp() && getNativePlatform() === "ios") {
              e.preventDefault();
              e.stopPropagation();
              await handleIOSPicker();
            }
          }}
        >
          <Dragger
            accept={accept}
            multiple={multiple}
            showUploadList={false}
            customRequest={processUpload}
            disabled={disabled}
            className={className}
            style={style}
          >
            {children}
          </Dragger>
        </div>
      )}
      {open && (
        <AndroidDrawer
          open={open}
          setOpen={setOpen}
          cameraClick={async () => {
            setOpen(false);
            const granted = await requestCameraPermission();
            if (!granted) {
              setPermissionModal({ visible: true, name: "Camera" });
              return;
            }
            cameraRef.current && cameraRef.current?.click();
          }}
          filesClick={async () => {
            setOpen(false);
            hiddenInputRef.current && hiddenInputRef.current.click();
          }}
        />
      )}
      <PermissionDeniedModal
        isModalOpen={permissionModal.visible}
        handleClose={() => setPermissionModal({ visible: false, name: "" })}
        permissionName={permissionModal.name}
      />
    </>
  );
};

type AndroidDrawerProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cameraClick: () => void;
  filesClick: () => void;
};

export const AndroidDrawer = ({
  open,
  setOpen,
  cameraClick,
  filesClick,
}: AndroidDrawerProps) => {
  return (
    <Drawer
      placement="bottom"
      open={open}
      onClose={() => setOpen(false)}
      height="auto"
      closable={false}
      maskClosable
    >
      <div className="text-black fw-bolder page-title gap-2 text-center">
        Choose from
      </div>
      <div className="d-flex justify-content-around mt-4">
        <div
          onClick={cameraClick}
          className="d-flex flex-column align-items-center gap-2 fs-6 cursor-pointer"
        >
          <CameraOutlined style={{ fontSize: 28 }} />
          <span className="custom-label">Camera</span>
        </div>
        <div
          onClick={filesClick}
          className="d-flex flex-column align-items-center gap-2 fs-6 cursor-pointer"
        >
          <FolderOpenOutlined style={{ fontSize: 28 }} />
          <span className="custom-label">Files</span>
        </div>
      </div>
      <button
        className="btn btn-lg btn-primary px-4 w-100 mt-4"
        onClick={() => setOpen(false)}
      >
        CANCEL
      </button>
    </Drawer>
  );
};

export default S3FileUploader;
